<template>
  <div>
    <!-- Search Bar -->
    <div class="el-search vue-top-padding radius">
      <el-form inline>
        <el-form-item>
          <el-input
            v-model="searchQuery"
            placeholder="Search pushed ads..."
            prefix-icon="el-icon-search"
            clearable
            style="width: 300px;"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="fetchCampaignAds">Search</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- Main Content -->
    <div class="vue-padding radius">
      <!-- Info Box -->
      <el-alert
        :title="isAdmin ? 'Admin: Network Ads' : 'View Network Ads'"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 18px;"
      >
        <template slot="default">
          <span v-if="isAdmin">
            Ads pushed from admin panel will appear on user machines as "Network Ads" that cannot be edited or deleted by users. They will be displayed alongside the user's own ad configurations.
          </span>
          <span v-else>
            Ads pushed from the VTM Network will appear in this space. They cannot be edited or deleted and will be displayed alongside user-created ads.
          </span>
        </template>
      </el-alert>

      <!-- Table -->
      <el-table
        :data="pagedAds"
        v-loading="loading"
        border
        max-height="638"
        style="width: 100%;"
      >
        <el-table-column label="Preview" width="90" align="center">
          <template slot-scope="{ row }">
            <template v-if="row.type === 1">
              <img
                v-if="campaignVideoThumbnail[row.adId != null ? row.adId : row.id]"
                :src="campaignVideoThumbnail[row.adId != null ? row.adId : row.id]"
                :alt="row.head"
                class="preview-thumb"
              >
              <video
                v-else
                :src="row.url"
                class="preview-thumb"
                autoplay
                muted
                loop
                playsinline
              />
            </template>
            <img v-else :src="row.url || row.thum" :alt="row.head" class="preview-thumb">
          </template>
        </el-table-column>

        <el-table-column prop="head" label="Ad Title" min-width="140">
          <template slot-scope="{ row }">
            <strong>{{ row.head }}</strong>
          </template>
        </el-table-column>

        <el-table-column prop="advertiser" label="Company" min-width="120" />

        <el-table-column label="Type" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.display === 1 ? 'primary' : 'warning'" size="small">
              {{ row.display === 1 ? 'Screen' : 'Banner' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Target" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" icon="el-icon-view" @click="openTargetAssignments(row)">View</el-button>
          </template>
        </el-table-column>

        <el-table-column label="Date Range" min-width="180">
          <template slot-scope="{ row }">
            <span v-if="row.startTime || row.endTime">
              {{ row.startTime ? row.startTime.slice(0, 10) : '—' }}
              <span class="date-arrow"> → </span>
              {{ row.endTime ? row.endTime.slice(0, 10) : '—' }}
            </span>
            <span v-else class="text-placeholder">{{ row.createTime }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="pushedBy" label="Pushed By" min-width="110">
          <template slot-scope="{ row }">{{ row.pushedBy || '—' }}</template>
        </el-table-column>

        <el-table-column label="Status" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag
              :type="getAdStatus(row) === 'active' ? 'success' : 'danger'"
              size="small"
            >
              {{ getAdStatus(row) === 'active' ? 'Active' : 'Inactive' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination -->
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="filteredAds.length"
        :page-size="pageSize"
        :current-page="pageIndex"
        :page-sizes="[10, 20, 50]"
        style="margin-top: 16px; text-align: right;"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- Target Assignments Dialog -->
    <el-dialog append-to-body
      title="Target Assignments"
      :visible.sync="showTargetAssignmentsModal"
      width="500px"
      @close="closeTargetAssignments"
    >
      <div v-if="targetAssignmentAd" class="target-ad-info">
        <img
          v-if="targetAssignmentAd.type !== 1"
          :src="targetAssignmentAd.url || targetAssignmentAd.thum"
          :alt="targetAssignmentAd.head"
          class="target-thumb"
        >
        <video
          v-else
          :src="targetAssignmentAd.url || targetAssignmentAd.thum"
          class="target-thumb"
          muted loop playsinline preload="metadata"
        />
        <div class="target-ad-text">
          <div class="target-ad-title">{{ targetAssignmentAd.head }}</div>
          <div class="target-ad-company">{{ targetAssignmentAd.advertiser }}</div>
        </div>
      </div>

      <div v-if="targetAssignmentAd" style="margin-top: 16px;">
        <template v-if="targetAssignmentDetails.target === 0">
          <el-tag type="info">All Machines</el-tag>
          <p style="margin-top: 8px; color: var(--vtm-ink-2); font-size: 13px;">
            This ad is pushed to all machines in the network
          </p>
        </template>
        <template v-else-if="targetAssignmentDetails.target === 1">
          <p style="font-weight: 600; margin-bottom: 8px;">Machine Groups</p>
          <div v-if="targetGroupLoading" style="color: var(--vtm-ink-2);">Loading...</div>
          <div v-else>
            <div
              v-for="(grp, idx) in targetGroupDetails"
              :key="grp.groupId || idx"
              class="target-list-item"
            >
              <span>{{ grp.groupName || grp.groupId }}</span>
              <span style="color: var(--vtm-ink-2); font-size: 12px;">
                {{ grp.totalMachines != null ? grp.totalMachines : 0 }} machines
              </span>
            </div>
            <div v-if="targetGroupDetails.length === 0" style="color: var(--vtm-ink-2);">
              No groups assigned
            </div>
          </div>
        </template>
        <template v-else-if="targetAssignmentDetails.target === 2">
          <p style="font-weight: 600; margin-bottom: 8px;">Machines</p>
          <div
            v-for="(m, idx) in targetAssignmentDetails.machines"
            :key="idx"
            class="target-list-item target-list-item-machine"
          >
            <div>
              <div style="font-size: 13px;">{{ m.equipmentName ? m.equipmentName + ' (' + (m.equipmentNo || '') + ')' : (m.equipmentNo || 'N/A') }}</div>
              <div style="font-size: 12px; color: var(--vtm-ink-2);">{{ m.address || 'N/A' }}</div>
            </div>
          </div>
          <div v-if="targetAssignmentDetails.machines.length === 0" style="color: var(--vtm-ink-2);">
            No machines assigned
          </div>
        </template>
        <template v-else>
          <p style="color: var(--vtm-ink-2);">—</p>
        </template>
      </div>

      <span slot="footer">
        <el-button type="primary" @click="closeTargetAssignments">Close</el-button>
      </span>
    </el-dialog>

    <!-- Edit Ad Dialog (admin) -->
    <el-dialog append-to-body
      title="Edit Paid Ad"
      :visible.sync="showEditAdModal"
      width="600px"
      @close="closeEditAdModal"
    >
      <el-form label-position="top">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="Title">
              <el-input v-model="editFormData.title" placeholder="Ad title" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Company">
              <el-input v-model="editFormData.company" placeholder="Company name" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="Start Date & Time">
              <el-date-picker
                v-model="editFormData.startDate"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss"
                placeholder="Select start date"
                style="width: 100%;"
                clearable
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="End Date & Time">
              <el-date-picker
                v-model="editFormData.endDate"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss"
                :default-time="endDateDefaultTime"
                placeholder="Select end date"
                style="width: 100%;"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Target">
          <el-radio-group v-model="editTargetOption">
            <el-radio-button label="all">All Machines</el-radio-button>
            <el-radio-button label="groups">Machine Groups</el-radio-button>
            <el-radio-button label="individual">Individual Machines</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <!-- Individual Machines -->
        <el-form-item v-if="editTargetOption === 'individual'">
          <el-input
            v-model="machineSearchQuery"
            placeholder="Search machines..."
            prefix-icon="el-icon-search"
            clearable
            style="margin-bottom: 8px;"
          />
          <div class="modal-list-scroll">
            <div
              v-for="machine in modalMachinesFiltered"
              :key="machine.id"
              class="modal-list-item"
              @click="toggleMachine(machine.id)"
            >
              <el-checkbox
                :value="editFormData.selectedMachines.indexOf(machine.id) !== -1"
                @change="toggleMachine(machine.id)"
                @click.native.stop
              />
              <div class="modal-list-item-text">
                <span>{{ machine.equipmentName ? machine.equipmentName + ' (' + (machine.equipmentNo || '') + ')' : (machine.equipmentNo || 'N/A') }}</span>
                <span style="font-size: 12px; color: var(--vtm-ink-2);">{{ machine.address }}</span>
              </div>
            </div>
            <div v-if="machineListLoading" style="text-align: center; color: var(--vtm-ink-2); padding: 12px;">Loading...</div>
          </div>
          <div style="font-size: 12px; color: var(--vtm-ink-2); margin-top: 4px;">
            {{ editFormData.selectedMachines.length }} selected
          </div>
        </el-form-item>

        <!-- Machine Groups -->
        <el-form-item v-if="editTargetOption === 'groups'">
          <el-input
            v-model="groupSearchQuery"
            placeholder="Search groups..."
            prefix-icon="el-icon-search"
            clearable
            style="margin-bottom: 8px;"
          />
          <div class="modal-list-scroll">
            <div
              v-for="group in filteredMachineGroups"
              :key="group.id"
              class="modal-list-item"
              @click="toggleGroup(group.id)"
            >
              <el-checkbox
                :value="editFormData.selectedGroups.indexOf(group.id) !== -1"
                @change="toggleGroup(group.id)"
                @click.native.stop
              />
              <div class="modal-list-item-text">
                <span>{{ group.name }}</span>
                <span style="font-size: 12px; color: var(--vtm-ink-2);">{{ group.machineCount }} machines</span>
              </div>
            </div>
          </div>
        </el-form-item>

        <div v-if="editTargetOption === 'all'" style="color: var(--vtm-ink-2); font-size: 13px; margin-bottom: 16px;">
          This ad will be pushed to all machines in the network
        </div>
      </el-form>

      <span slot="footer">
        <el-button @click="closeEditAdModal">Cancel</el-button>
        <el-button type="primary" @click="saveEditedAd">Save Changes</el-button>
      </span>
    </el-dialog>

    <!-- Upload Ad Dialog (admin) -->
    <el-dialog append-to-body
      title="Upload Paid Ad"
      :visible.sync="showUploadAdModal"
      width="560px"
      @close="closeUploadAdModal"
    >
      <div
        v-if="!uploadFormData.uploadedFileUrl"
        class="upload-drop-zone"
        :class="{ active: dragOver }"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="handleFileDrop"
        @click="triggerFileInput"
      >
        <i class="el-icon-upload" style="font-size: 40px; color: var(--vtm-mint-ink);"></i>
        <p style="margin: 8px 0 4px; font-size: 14px; color: var(--vtm-ink);">
          Drag &amp; drop media files here or click to browse
        </p>
        <p style="font-size: 12px; color: var(--vtm-ink-2);">Supports JPG, PNG, MP4, WebM</p>
        <input
          type="file"
          ref="fileInput"
          style="display: none;"
          accept=".jpg,.jpeg,.png,.gif,.mp4,.webm"
          @change="handleFileSelect"
        >
      </div>

      <div v-if="uploadFormData.uploadedFileUrl" class="upload-preview">
        <img
          v-if="uploadFormData.mediaType === 'image'"
          :src="uploadFormData.uploadedFileUrl"
          style="max-height: 150px; border-radius: var(--radius-sm); object-fit: cover;"
        >
        <video
          v-else-if="uploadFormData.mediaType === 'video'"
          :src="uploadFormData.uploadedFileUrl"
          controls
          style="width: 100%; height: 150px; border-radius: var(--radius-sm);"
        />
        <div style="margin-top: 8px;">
          <span style="font-size: 13px; color: var(--vtm-ink);">
            <i class="el-icon-circle-check" style="color: var(--vtm-mint-ink);"></i>
            <strong v-if="uploadFormData.file">{{ uploadFormData.file.name }}</strong>
            <strong v-else>File uploaded</strong>
          </span>
          <el-button type="text" icon="el-icon-delete" @click="clearUploadedFile" style="margin-left: 8px;">Clear</el-button>
        </div>
      </div>

      <el-form label-position="top" style="margin-top: 16px;">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="Title">
              <el-input v-model="uploadFormData.title" placeholder="Ad title" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Company">
              <el-input v-model="uploadFormData.company" placeholder="Company name" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Placement Type">
          <el-radio-group v-model="uploadFormData.placementType">
            <el-radio-button label="screen">
              <i class="el-icon-monitor"></i> Screen
            </el-radio-button>
            <el-radio-button label="banner">
              <i class="el-icon-picture"></i> Banner
            </el-radio-button>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="closeUploadAdModal">Cancel</el-button>
        <el-button type="primary" @click="uploadAd">Upload</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from "@/interface/index";
import url from "@/interface/url";
import methods from "@/interface/method";
import Cookies from "js-cookie";

export default {
  name: 'ViewNetworkAds',
  data() {
    return {
      activeTab: 'campaigns',
      searchQuery: '',
      showEditAdModal: false,
      showTargetAssignmentsModal: false,
      targetAssignmentAd: null,
      targetGroupDetails: [],
      targetGroupLoading: false,
      editingAd: null,
      editFormData: {
        title: '',
        company: '',
        startDate: '',
        endDate: '',
        target: 'all',
        selectedMachines: [],
        selectedGroups: []
      },
      editTargetOption: 'all',
      machineSearchQuery: '',
      groupSearchQuery: '',
      individualMachines: [],
      modalMachineList: [],
      machineListPage: { pageIndex: 1, pageSize: 10, pages: 1 },
      machineListLoading: false,
      machineListBackgroundLoading: false,
      machineGroups: [],
      showUploadAdModal: false,
      uploadFormData: {
        title: '',
        company: '',
        mediaType: 'video',
        placementType: 'screen',
        file: null,
        uploadedFileUrl: null,
        videoDuration: 30
      },
      dragOver: false,
      loading: false,
      ads: [],
      campaignVideoThumbnail: {},
      pageIndex: 1,
      pageSize: 10,
      endDateDefaultTime: '23:59:59'
    };
  },
  mounted() {
    this.fetchCampaignAds();
    this.fetchMachineGroups();
    this.fetchAllMachinesOnLoad();
  },
  watch: {
    filteredAds: {
      handler(ads) {
        const adIds = new Set((ads || []).map(a => a.adId != null ? a.adId : a.id));
        Object.keys(this.campaignVideoThumbnail).forEach(id => {
          if (!adIds.has(id)) this.$delete(this.campaignVideoThumbnail, id);
        });
        (ads || []).filter(ad => ad.type === 1 && ad.url).forEach(ad => {
          const key = ad.adId != null ? ad.adId : ad.id;
          if (!this.campaignVideoThumbnail[key]) this.captureVideoThumb(ad);
        });
      },
      immediate: true
    }
  },
  computed: {
    isAdmin() {
      return Cookies.get('roleId') === '0';
    },
    filteredAds() {
      if (!this.ads || this.ads.length === 0) return [];
      let filtered = this.ads;
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(ad =>
          (ad.head || '').toLowerCase().includes(q) ||
          (ad.advertiser || '').toLowerCase().includes(q)
        );
      }
      return filtered;
    },
    pagedAds() {
      const start = (this.pageIndex - 1) * this.pageSize;
      return this.filteredAds.slice(start, start + this.pageSize);
    },
    modalMachinesFiltered() {
      if (!this.machineSearchQuery) return this.modalMachineList;
      const q = this.machineSearchQuery.toLowerCase();
      return this.modalMachineList.filter(m =>
        (m.equipmentNo || '').toLowerCase().includes(q) ||
        (m.address || '').toLowerCase().includes(q) ||
        (m.equipmentName || '').toLowerCase().includes(q) ||
        (m.group || '').toLowerCase().includes(q)
      );
    },
    targetAssignmentDetails() {
      const ad = this.targetAssignmentAd;
      if (!ad || !this.ads) return { target: null, machines: [], groups: [], groupIds: [] };
      const rows = this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id));
      const target = ad.target != null ? ad.target : (rows[0] && rows[0].target);
      const equipmentIds = [...new Set(rows.flatMap(r => {
        if (Array.isArray(r.equipmentIdList) && r.equipmentIdList.length > 0) return r.equipmentIdList.filter(Boolean);
        const e = r.equipmentId != null ? r.equipmentId : r.equipmentIds;
        if (Array.isArray(e)) return e.filter(Boolean);
        return e != null && e !== '' ? [e] : [];
      }))];
      const groupIds = [...new Set(rows.map(r => r.groupId).filter(Boolean))];
      const machineList = this.individualMachines && this.individualMachines.length > 0 ? this.individualMachines : this.modalMachineList;
      const machines = equipmentIds.map(eid => {
        const m = machineList.find(x => String(x.id) === String(eid)) || machineList.find(x => String(x.equipmentNo) === String(eid));
        if (!m) return { equipmentNo: String(eid), equipmentName: 'N/A', address: 'N/A' };
        return {
          equipmentNo: (m.equipmentNo != null && m.equipmentNo !== '') ? m.equipmentNo : 'N/A',
          equipmentName: (m.equipmentName != null && m.equipmentName !== '') ? m.equipmentName : ((m.equipmentNo != null && m.equipmentNo !== '') ? m.equipmentNo : 'N/A'),
          address: (m.address != null && m.address !== '') ? m.address : 'N/A'
        };
      });
      return { target, machines, groups: [], groupIds };
    },
    filteredMachineGroups() {
      if (!this.groupSearchQuery) return this.machineGroups;
      return this.machineGroups.filter(g => g.name.toLowerCase().includes(this.groupSearchQuery.toLowerCase()));
    }
  },
  methods: {
    handleSizeChange(val) {
      this.pageSize = val;
      this.pageIndex = 1;
    },
    handleCurrentChange(val) {
      this.pageIndex = val;
    },
    toggleMachine(id) {
      const idx = this.editFormData.selectedMachines.indexOf(id);
      if (idx === -1) this.editFormData.selectedMachines.push(id);
      else this.editFormData.selectedMachines.splice(idx, 1);
    },
    toggleGroup(id) {
      const idx = this.editFormData.selectedGroups.indexOf(id);
      if (idx === -1) this.editFormData.selectedGroups.push(id);
      else this.editFormData.selectedGroups.splice(idx, 1);
    },
    getAdStatus(ad) {
      if (!ad.startTime && !ad.endTime) return 'active';
      const now = new Date();
      const start = ad.startTime ? new Date(ad.startTime) : null;
      const end = ad.endTime ? new Date(ad.endTime) : null;
      if (start && now < start) return 'inactive';
      if (end && now > end) return 'inactive';
      return 'active';
    },
    captureVideoThumb(ad) {
      const key = ad && (ad.adId != null ? ad.adId : ad.id);
      if (!ad || !key || !ad.url || this.campaignVideoThumbnail[key]) return;
      const v = document.createElement('video');
      v.muted = true;
      v.playsInline = true;
      v.preload = 'metadata';
      v.crossOrigin = 'anonymous';
      const onDone = () => { v.src = ''; v.load(); };
      v.addEventListener('loadeddata', () => { v.currentTime = 0; });
      v.addEventListener('seeked', () => {
        try {
          const c = document.createElement('canvas');
          c.width = v.videoWidth;
          c.height = v.videoHeight;
          c.getContext('2d').drawImage(v, 0, 0);
          this.$set(this.campaignVideoThumbnail, key, c.toDataURL('image/jpeg', 0.85));
        } catch (e) { /* CORS fallback */ }
        onDone();
      });
      v.addEventListener('error', onDone);
      v.src = ad.url;
      v.load();
    },
    fetchCampaignAds() {
      this.loading = true;
      request.request(url.userEquipmentAd.adsList, methods.get, { adType: 1 })
        .then(response => {
          if (response.code === 0) {
            this.ads = response.data || [];
          } else {
            this.$message.error(response.msg || 'Failed to load campaign ads');
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching campaign ads:', error);
          this.$message.error('Error fetching campaign ads');
          this.loading = false;
        });
    },
    fetchMachineGroups() {
      request.request(url.UserMachineGroup.fetchGroupMachines, methods.get, {})
        .then(response => {
          if (response.code === 0) {
            const groups = response.data || [];
            this.machineGroups = groups.map(item => ({
              id: item.groupId,
              name: item.groupName,
              createdDate: item.createdDate ? String(item.createdDate).slice(0, 10) : '',
              machineCount: item.machineCount != null ? item.machineCount : 0,
              selected: false
            }));
          } else {
            this.$message.error(response.msg || 'Failed to load machine groups');
          }
        })
        .catch(error => {
          console.error('Error fetching machine groups:', error);
        });
    },
    mapSelectRecordToMachine(record) {
      const id = record.equipmentId != null ? record.equipmentId : record.id;
      const equipmentNo = record.equipmentNum != null ? record.equipmentNum : record.equipmentNo;
      const equipmentName = (record.equipmentName != null ? record.equipmentName : record.name) || equipmentNo || '';
      const address = record.locale != null && record.locale !== '' ? record.locale : '';
      return {
        id,
        equipmentNo: equipmentNo != null ? equipmentNo : '',
        equipmentName: equipmentName != null ? equipmentName : '',
        address,
        group: record.teamName != null ? record.teamName : '',
        selected: false
      };
    },
    fetchAllMachinesOnLoad() {
      this.machineListBackgroundLoading = true;
      request.request(url.UserMachine.fetchMachineDetails, methods.get, {})
        .then(res => {
          if (res.code === 0 && res.data) {
            const list = Array.isArray(res.data) ? res.data : (res.data.records || []);
            const mapped = list.map(item => this.mapSelectRecordToMachine(item));
            this.modalMachineList = mapped;
            this.individualMachines = mapped;
            this.machineListPage.pages = 1;
            this.machineListPage.pageIndex = 1;
          }
        })
        .catch(() => {})
        .finally(() => { this.machineListBackgroundLoading = false; });
    },
    openEditAdModal(ad) {
      this.editingAd = ad;
      const target = ad.target != null ? ad.target : 0;
      const targetOption = target === 0 ? 'all' : target === 1 ? 'groups' : 'individual';
      let selectedGroups = [];
      let selectedMachines = [];
      if (this.ads && (target === 1 || target === 2)) {
        const rows = this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id));
        if (target === 1) {
          selectedGroups = [...new Set(rows.flatMap(r => {
            const g = r.groupId;
            if (Array.isArray(g)) return g.filter(Boolean);
            return g != null && g !== '' ? [g] : [];
          }))];
        } else if (target === 2) {
          selectedMachines = [...new Set(rows.flatMap(r => {
            const e = r.equipmentId;
            if (Array.isArray(e)) return e.filter(Boolean).map(String);
            return e != null && e !== '' ? [String(e)] : [];
          }))];
        }
      }
      const startTimeStr = ad.startTime ? String(ad.startTime).trim() : '';
      const endTimeStr = ad.endTime ? String(ad.endTime).trim() : '';
      this.editFormData = {
        id: ad.id,
        title: ad.head,
        company: ad.advertiser,
        startDate: startTimeStr ? (startTimeStr.length >= 19 ? startTimeStr : startTimeStr + ' 00:00:00') : '',
        endDate: endTimeStr ? (endTimeStr.length >= 19 ? endTimeStr : endTimeStr + ' 23:59:59') : '',
        target: targetOption,
        selectedMachines,
        selectedGroups
      };
      this.editTargetOption = targetOption;
      this.machineSearchQuery = '';
      this.groupSearchQuery = '';
      this.showEditAdModal = true;
    },
    closeEditAdModal() {
      this.showEditAdModal = false;
      this.editingAd = null;
      this.editFormData = {
        title: '', company: '', startDate: '', endDate: '',
        target: 'all', selectedMachines: [], selectedGroups: []
      };
    },
    openTargetAssignments(ad) {
      this.targetAssignmentAd = ad;
      this.showTargetAssignmentsModal = true;
      this.targetGroupDetails = [];
      const target = ad.target != null ? ad.target : 0;
      if (target === 1 && this.ads) {
        const rows = this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id));
        const groupIds = [...new Set(rows.flatMap(r => {
          const g = r.groupId;
          if (Array.isArray(g)) return g.filter(Boolean);
          return g != null && g !== '' ? [g] : [];
        }))];
        if (groupIds.length > 0) this.fetchTargetGroupDetails(groupIds);
      }
    },
    fetchTargetGroupDetails(groupIds) {
      this.targetGroupLoading = true;
      request.request(url.UserMachineGroup.getMachineGroup, methods.post, groupIds)
        .then(response => {
          this.targetGroupDetails = (response.code === 0 && Array.isArray(response.data)) ? response.data : [];
        })
        .catch(() => { this.targetGroupDetails = []; })
        .finally(() => { this.targetGroupLoading = false; });
    },
    closeTargetAssignments() {
      this.showTargetAssignmentsModal = false;
      this.targetAssignmentAd = null;
      this.targetGroupDetails = [];
      this.targetGroupLoading = false;
    },
    saveEditedAd() {
      if (!this.editFormData.title || !this.editFormData.company) {
        this.$message.error('Please fill in all required fields');
        return;
      }
      const targetMap = { all: 0, groups: 1, individual: 2 };
      const target = targetMap[this.editTargetOption];
      if (target === 1 && (!this.editFormData.selectedGroups || this.editFormData.selectedGroups.length === 0)) {
        this.$message.error('Please select at least one machine group');
        return;
      }
      if (target === 2 && (!this.editFormData.selectedMachines || this.editFormData.selectedMachines.length === 0)) {
        this.$message.error('Please select at least one machine');
        return;
      }
      if (!this.editFormData.startDate || !this.editFormData.endDate) {
        this.$message.error('Please select start and end date');
        return;
      }
      if (new Date(this.editFormData.endDate).getTime() < new Date(this.editFormData.startDate).getTime()) {
        this.$message.error('End date must be after start date');
        return;
      }
      const ad = this.editingAd;
      const rows = this.ads ? this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id)) : [ad];
      const assignmentIds = rows.map(r => r.id);
      const flatStrIds = (arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.flat(Infinity).map(x => (x != null && x !== '' ? String(x) : null)).filter(Boolean);
      };
      const payload = {
        assignmentId: ad.assignmentId,
        target,
        startTime: this.editFormData.startDate,
        endTime: this.editFormData.endDate,
        pushedBy: ad.pushedBy != null ? ad.pushedBy : '',
        adId: ad.adId != null ? ad.adId : ad.id,
        sort: ad.sort != null ? ad.sort : 1,
        head: this.editFormData.title,
        advertiser: this.editFormData.company,
        groupId: target === 1 ? flatStrIds(this.editFormData.selectedGroups) : [],
        equipmentId: target === 2 ? flatStrIds(this.editFormData.selectedMachines) : [],
        id: assignmentIds,
        uId: ad.uId != null ? ad.uId : (ad.uid != null ? ad.uid : '1')
      };
      request.request(url.userEquipmentAd.editPaidAd, methods.post, payload)
        .then(response => {
          if (response.code === 0) {
            this.$message.success('Ad updated successfully!');
            this.closeEditAdModal();
            this.fetchCampaignAds();
          } else {
            this.$message.error(response.msg || 'Failed to update ad');
          }
        })
        .catch(error => {
          console.error('Error updating ad:', error);
          this.$message.error('Error updating ad');
        });
    },
    openUploadAdModal() {
      this.showUploadAdModal = true;
      this.uploadFormData = {
        title: '', company: '', mediaType: 'video', placementType: 'screen',
        file: null, uploadedFileUrl: null, videoDuration: 30
      };
      this.dragOver = false;
    },
    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    clearUploadedFile() {
      this.uploadFormData.uploadedFileUrl = null;
      this.uploadFormData.file = null;
      this.uploadFormData.videoDuration = 30;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    closeUploadAdModal() {
      this.showUploadAdModal = false;
      this.uploadFormData = {
        title: '', company: '', mediaType: 'video', placementType: 'screen',
        file: null, uploadedFileUrl: null, videoDuration: 30
      };
      this.dragOver = false;
    },
    handleFileDrop(event) {
      const files = event.dataTransfer.files;
      if (files.length > 0) this.handleFileValidationAndUpload(files[0]);
      this.dragOver = false;
    },
    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length > 0) this.handleFileValidationAndUpload(files[0]);
    },
    handleFileValidationAndUpload(file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/webm'];
      const allowedExtensions = /\.(jpg|jpeg|png|gif|mp4|webm)$/i;
      if (!allowedTypes.includes(file.type) && !allowedExtensions.test(file.name)) {
        this.$message.error('Unsupported file type. Please upload JPG, PNG, MP4, or WebM files only');
        return;
      }
      const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name);
      const isVideo = /\.(mp4|webm)$/i.test(file.name);
      if (isImage && file.size > 5 * 1024 * 1024) {
        this.$message.error(`Image size must not exceed 5MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }
      if (isVideo && file.size > 100 * 1024 * 1024) {
        this.$message.error(`Video size must not exceed 100MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }
      this.uploadFormData.file = file;
      if (isImage) this.uploadFormData.mediaType = 'image';
      else if (isVideo) this.uploadFormData.mediaType = 'video';
      this.uploadFileToS3(file);
    },
    uploadFileToS3(file) {
      const formData = new FormData();
      formData.append('file', file);
      this.loading = true;
      this.$message({ message: 'Uploading file to server...', type: 'info', duration: 0 });
      request.upload(url.Upload.advert, formData)
        .then(response => {
          this.loading = false;
          if (response.code === 0) {
            this.uploadFormData.uploadedFileUrl = response.data;
            this.$message.success('File uploaded successfully!');
            if (this.uploadFormData.mediaType === 'video') this.getVideoDuration(response.data);
          } else {
            this.$message.error(response.msg || 'File upload failed');
            this.uploadFormData.file = null;
            this.uploadFormData.uploadedFileUrl = null;
          }
        })
        .catch(error => {
          this.loading = false;
          console.error('Error uploading file:', error);
          this.$message.error('Error uploading file to server');
          this.uploadFormData.file = null;
          this.uploadFormData.uploadedFileUrl = null;
        });
    },
    getVideoDuration(videoUrl) {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.addEventListener('loadedmetadata', () => {
        this.uploadFormData.videoDuration = Math.ceil(video.duration);
      });
      video.addEventListener('error', () => {
        this.uploadFormData.videoDuration = 30;
      });
    },
    uploadAd() {
      if (!this.uploadFormData.title || !this.uploadFormData.company) {
        this.$message.error('Please fill in all required fields (Title and Company)');
        return;
      }
      if (!this.uploadFormData.uploadedFileUrl) {
        this.$message.error('Please select and upload a file first');
        return;
      }
      const adData = {
        head: this.uploadFormData.title,
        advertiser: this.uploadFormData.company,
        url: this.uploadFormData.uploadedFileUrl,
        type: this.uploadFormData.mediaType === 'video' ? 1 : 0,
        display: this.uploadFormData.placementType === 'screen' ? 1 : 2,
        len: this.uploadFormData.videoDuration || (this.uploadFormData.mediaType === 'video' ? 30 : 15),
        thum: this.uploadFormData.uploadedFileUrl,
        adType: 1
      };
      request.request(url.Adverts.insert, methods.post, adData)
        .then(response => {
          if (response.code === 0) {
            this.$message.success('Ad created successfully!');
            this.closeUploadAdModal();
            this.fetchCampaignAds();
          } else {
            this.$message.error(response.msg || 'Failed to create ad');
          }
        })
        .catch(error => {
          console.error('Error creating ad:', error);
          this.$message.error('Error creating ad');
        });
    },
    deleteAd(ad) {
      let resolvedId = null;
      if (ad != null && typeof ad === 'object') resolvedId = ad.assignmentId;
      else if (ad != null) resolvedId = ad;
      this.$confirm('Are you sure you want to delete this ad?', 'Warning', {
        confirmButtonText: 'OK', cancelButtonText: 'Cancel', type: 'warning'
      }).then(() => {
        if (resolvedId == null || resolvedId === '') {
          this.$message.error('Could not determine ad id to delete');
          return;
        }
        const endpoint = `${url.userEquipmentAd.removeAds}?assignmentId=${encodeURIComponent(resolvedId)}`;
        request.request(endpoint, methods.delete, {})
          .then(response => {
            if (response.code === 0) {
              this.$message.success('Ad deleted successfully!');
              this.fetchCampaignAds();
            } else {
              this.$message.error(response.msg || 'Failed to delete ad');
            }
          })
          .catch(error => {
            console.error('Error deleting ad:', error);
            this.$message.error('Error deleting ad');
          });
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.preview-thumb {
  width: 60px;
  height: 60px;
  border-radius: var(--radius-sm);
  object-fit: cover;
  border: 1px solid var(--vtm-line);
  display: block;
  margin: 0 auto;
}

.date-arrow {
  color: var(--vtm-ink-3);
  font-size: 11px;
}

.text-placeholder {
  color: var(--vtm-ink-3);
  font-size: 12px;
}

/* Target Assignments dialog */
.target-ad-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.target-thumb {
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: var(--radius-sm);
  border: 1px solid var(--vtm-line);
  flex-shrink: 0;
}

.target-ad-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.target-ad-company {
  font-size: 13px;
  color: var(--vtm-ink-2);
}

.target-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid var(--vtm-line);
  border-radius: var(--radius-sm);
  margin-bottom: 6px;
  background: var(--vtm-surface);
}

.target-list-item-machine {
  flex-direction: column;
  align-items: flex-start;
}

/* Modal machine/group list */
.modal-list-scroll {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--vtm-line);
  border-radius: var(--radius-sm);
}

.modal-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--vtm-line);
  transition: background 0.2s;
}

.modal-list-item:last-child {
  border-bottom: none;
}

.modal-list-item:hover {
  background: var(--vtm-mint-soft);
}

.modal-list-item-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
}

/* Upload drop zone */
.upload-drop-zone {
  border: 2px dashed var(--vtm-line);
  border-radius: var(--radius-md);
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.upload-drop-zone:hover,
.upload-drop-zone.active {
  border-color: var(--vtm-mint);
  background: var(--vtm-mint-soft);
}

.upload-preview {
  text-align: center;
  padding: 12px;
  border: 1px solid var(--vtm-line);
  border-radius: var(--radius-sm);
  margin-bottom: 8px;
}
</style>
